import { useCallback, useMemo, useRef } from 'react';

/** The recorder `useRecording()` drives: desktop mic, web, or a Heidi Remote device. */
export type RecordingTransport = 'v1' | 'v2' | 'chronicle';

/** The recorder call a stop runs. */
export type RecordingStopRoute = 'stopFromPause' | 'stopRecording';

export type RecordingStopDecision =
  | { accepted: false }
  | { accepted: true; route: RecordingStopRoute };

export type RecordingCommandPolicy = {
  acceptPause: () => boolean;
  acceptStop: (fromPaused: boolean) => RecordingStopDecision;
};

/** How long the desktop mic recorder ignores a pause or live stop after the last one. */
const RECORDING_COMMAND_WINDOW_MS = 1000;

/** A device stays live while paused, so only its full stop ends it. */
export const resolveRecordingStopRoute = (
  transport: RecordingTransport,
  fromPaused: boolean
): RecordingStopRoute =>
  fromPaused && transport !== 'chronicle' ? 'stopFromPause' : 'stopRecording';

/**
 * Whether the active recorder accepts a pause or stop press, and which stop it
 * runs. Only the desktop mic rejects: a pause or live stop within the window of
 * the last one, each press restarting it. Stop from pause is never in it.
 */
export const useRecordingCommandPolicy = (
  transport: RecordingTransport
): RecordingCommandPolicy => {
  const lastGatedPressAt = useRef<number | null>(null);

  const passesWindow = useCallback(() => {
    if (transport !== 'v1') return true;
    const now = Date.now();
    const last = lastGatedPressAt.current;
    lastGatedPressAt.current = now;
    return last === null || now - last >= RECORDING_COMMAND_WINDOW_MS;
  }, [transport]);

  const acceptPause = useCallback(() => passesWindow(), [passesWindow]);

  const acceptStop = useCallback(
    (fromPaused: boolean): RecordingStopDecision => {
      const route = resolveRecordingStopRoute(transport, fromPaused);
      if (route === 'stopRecording' && !passesWindow()) return { accepted: false };
      return { accepted: true, route };
    },
    [transport, passesWindow]
  );

  return useMemo(() => ({ acceptPause, acceptStop }), [acceptPause, acceptStop]);
};
